import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import type { SerializeFrom } from '~/types/serialize-from';

import { RoccoLink } from '@rocco/components/link';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared';
import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import {
    ABOUT_SECTION_CAP_CLASS,
    ABOUT_SECTION_CLASS,
    ABOUT_SECTION_HEADING_CLASS,
} from './about-section-heading';

type AboutLoaderData = SerializeFrom<typeof loader>;

const AWARDS_INITIAL = 8;
const AWARDS_STEP = 8;

/** The awards as grey rows (year / organisation / award / project + View Project). */
const AwardsRowContent = ({
    awards,
}: {
    awards: AboutLoaderData['awards'];
}) => {
    const [visible, setVisible] = useState(AWARDS_INITIAL);
    // Newest first. `year` is free text from the CMS, so anything that isn't a
    // number sorts last rather than poisoning the comparison; awards sharing a
    // year keep the CMS order (sort is stable). Sorted before the slice, so
    // "Show more" reveals older awards instead of reshuffling the visible ones.
    const sorted = useMemo(
        () =>
            [...awards].sort((a, b) => {
                const yearA = Number(a.year);
                const yearB = Number(b.year);
                const validA = Number.isFinite(yearA);
                const validB = Number.isFinite(yearB);
                if (!validA || !validB) return Number(validB) - Number(validA);
                return yearB - yearA;
            }),
        [awards],
    );
    const shown = sorted.slice(0, visible);

    return (
        <div className="flex flex-col gap-[0.25rem] max-md:pt-6 max-md:pb-6 md:pt-[5rem] md:pb-[5rem]">
            {shown.map((award) => {
                // AwardedEntities also carries the professional itself — the
                // Project column wants the live awarded Project/Product only
                // (mirrors the legacy info-page loader's filtering).
                const project = award.awardedEntities?.edges?.find(
                    (edge) =>
                        (edge.node.__typename === 'Project' ||
                            edge.node.__typename === 'Product') &&
                        'IsLive' in edge.node &&
                        edge.node.IsLive === true,
                )?.node;
                // AwardedEntities is a union — Article and BuildingGuide carry
                // no thumbnail, and the IsLive filter above does not narrow it.
                const preview =
                    project && 'Thumbnail' in project
                        ? project.Thumbnail?.desktop
                        : undefined;
                const cta =
                    project?.__typename === 'Product'
                        ? 'View Product'
                        : 'View Project';
                const row = (
                    <>
                        <span className="font-sans leading-tight font-medium text-[#1f1f21] max-md:text-xl md:text-[1.375rem]">
                            {award.year}
                        </span>
                        <AwardField
                            label="Organisation"
                            value={award.institution}
                        />
                        <AwardField label="Award" value={award.title} />
                        <AwardField
                            label={
                                project?.__typename === 'Product'
                                    ? 'Product'
                                    : 'Project'
                            }
                            value={project?.Title}
                        />
                        {project?.Link ? (
                            // A span, not a link: the whole row is already the
                            // link, and a link inside a link is neither valid
                            // nor reachable. `group/cta` is what the preview
                            // opens on, so it answers to this control alone
                            // rather than to anywhere on the row.
                            <span className="group/cta relative inline-flex h-10 items-center justify-center rounded-[var(--radius-btn-m)] border-[1.347px] border-[#181818] px-[1.18rem] font-sans text-base font-medium whitespace-nowrap text-[#181818] transition-colors group-hover/row:bg-[#181818] group-hover/row:text-white max-md:justify-self-start">
                                {cta}
                                {preview ? (
                                    // Sits to the control's left, centred on
                                    // it, and floats over the rows either
                                    // side; `pointer-events-none` so it can
                                    // never swallow the click it is
                                    // previewing. Desktop only — there is no
                                    // hover to open it on touch.
                                    <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute top-1/2 right-[calc(100%+0.75rem)] z-30 block w-[26.6rem] -translate-y-1/2 overflow-hidden rounded-tile opacity-0 shadow-[0_8px_28px_rgba(0,0,0,0.18)] transition-opacity duration-200 group-hover/cta:opacity-100 max-md:hidden"
                                    >
                                        <img
                                            src={preview}
                                            alt=""
                                            loading="lazy"
                                            className="block aspect-[425/289] w-full object-cover"
                                        />
                                    </span>
                                ) : null}
                            </span>
                        ) : (
                            <span />
                        )}
                    </>
                );
                const rowClassName =
                    'group/row relative grid items-center gap-x-[1.5rem] gap-y-3 rounded-tile-card bg-white transition-colors max-md:px-5 max-md:py-5 md:min-h-[6.25rem] md:grid-cols-[13%_19%_26%_1fr_auto] md:px-[2.5rem]';

                // Rows whose award has no live project have nowhere to go, so
                // they stay plain markup rather than a dead link.
                return project?.Link ? (
                    <RoccoLink
                        key={award.id}
                        to={project.Link}
                        className={cn(rowClassName, 'hover:bg-[#fafafa]')}
                    >
                        {row}
                    </RoccoLink>
                ) : (
                    <div key={award.id} className={rowClassName}>
                        {row}
                    </div>
                );
            })}
            {visible < awards.length ? (
                <LoadMoreButton
                    onClick={() => setVisible((value) => value + AWARDS_STEP)}
                    className="pt-[2.5rem]"
                />
            ) : null}
        </div>
    );
};

const AwardField = ({
    label,
    value,
}: {
    label: string;
    value?: string | null;
}) =>
    value ? (
        <div className="flex min-w-0 flex-col gap-[0.375rem]">
            <span className="font-sans leading-none text-[#767676] max-md:text-xs md:text-[0.875rem] md:tracking-[-0.026rem]">
                {label}
            </span>
            <span className="truncate font-sans leading-none font-medium text-[#141414] max-md:text-sm md:text-[1.25rem] md:tracking-[-0.025rem]">
                {value}
            </span>
        </div>
    ) : (
        <span />
    );

/**
 * Awards, as a section of its own under About the practice — headed in the
 * overview's own voice rather than numbered as a row, and standing open,
 * because a practice's awards are the tab's strongest claim.
 */
export const ProfessionalAboutAwardsSection = () => {
    const { awards } = useLoaderData<typeof loader>();
    if (awards.length === 0) return null;

    return (
        <section className={ABOUT_SECTION_CLASS}>
            <div className={ABOUT_SECTION_CAP_CLASS}>
                <h2 className={ABOUT_SECTION_HEADING_CLASS}>Awards</h2>
                <div className="max-md:pt-8 md:pt-[3rem]">
                    <AwardsRowContent awards={awards} />
                </div>
            </div>
        </section>
    );
};
